import { ProgressBarStatus } from '@amzn/stencil-react-components/progress-bar';
import { Status } from '@amzn/stencil-react-components/status-indicator';

export const RISK_STATUS_MESSAGE_KEY = {
  healthy: 'statusHealthy',
  watch: 'statusWatch',
  atRisk: 'statusAtRisk',
  critical: 'statusCritical',
  noData: 'statusNoData',
} as const;

export type RiskStatusMessageKey =
  (typeof RISK_STATUS_MESSAGE_KEY)[keyof typeof RISK_STATUS_MESSAGE_KEY];

interface KnownRiskStatusReading {
  readonly status: Status;
  readonly messageKey: RiskStatusMessageKey;
}

const RISK_WARNING_STATUS = Status.Warning;

const RISK_STATUS_READING = new Map<string, KnownRiskStatusReading>([
  ['HEALTHY', { status: Status.Positive, messageKey: RISK_STATUS_MESSAGE_KEY.healthy }],
  ['WATCH', { status: RISK_WARNING_STATUS, messageKey: RISK_STATUS_MESSAGE_KEY.watch }],
  [
    'AT RISK',
    { status: RISK_WARNING_STATUS, messageKey: RISK_STATUS_MESSAGE_KEY.atRisk },
  ],
  ['CRITICAL', { status: Status.Negative, messageKey: RISK_STATUS_MESSAGE_KEY.critical }],
  ['NO DATA', { status: Status.Unavailable, messageKey: RISK_STATUS_MESSAGE_KEY.noData }],
]);

export function riskStatusReading(status: string): KnownRiskStatusReading | undefined {
  return RISK_STATUS_READING.get(status.trim().toUpperCase());
}

/**
 * The semantic colour a risk status maps to for a `ProgressBar` (drives the drill-down driver
 * bars). Reuses the same status reading as the indicator so colour stays consistent; unknown or
 * no-data statuses fall back to neutral.
 */
export function riskProgressStatus(status: string): ProgressBarStatus {
  switch (riskStatusReading(status)?.status) {
    case Status.Positive:
      return ProgressBarStatus.Positive;
    case RISK_WARNING_STATUS:
      return ProgressBarStatus.Warning;
    case Status.Negative:
      return ProgressBarStatus.Negative;
    default:
      return ProgressBarStatus.Neutral;
  }
}

const NUMBER_FORMATTER = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
});

/** The source's marker for a unitless count/score — rendered as a bare number. */
const UNITLESS_METRIC_UNIT = 'number';

/**
 * Formats an actual/target value with the metric's unit, exactly as the source names it: `%` is
 * appended directly (`2.26%`); any other unit follows a space (`165 seconds`, `299.46 PPH`); the
 * source's unitless marker (`number`) and an absent unit render the bare number. Em dash when the
 * value is absent.
 */
export function formatRiskValue(value: number | undefined, metricUnit?: string): string {
  if (value === undefined) return '—';
  const formatted = formatRiskNumber(value);
  const unit = riskDisplayUnit(metricUnit);
  if (!unit) return formatted;
  return unit === '%' ? `${formatted}%` : `${formatted} ${unit}`;
}

/** The bare number (up to two decimals), for layouts that render the unit separately. */
export function formatRiskNumber(value: number): string {
  return NUMBER_FORMATTER.format(value);
}

/**
 * The unit to show beside a value, exactly as the source names it — or undefined when the source
 * gives none or marks the metric unitless (`number`).
 */
export function riskDisplayUnit(metricUnit?: string): string | undefined {
  const unit = metricUnit?.trim();
  if (!unit || unit.toLowerCase() === UNITLESS_METRIC_UNIT) return undefined;
  return unit;
}

/** Placeholder rows every Risk Engine table shows while it loads, so both pages shimmer alike. */
export const RISK_TABLE_SKELETON_ROW_COUNT = 5;

/** Separator for single-line metadata (e.g. `Target: 2.26% · % to target: 39.37%`). */
export const META_SEPARATOR = ' · ';

/** Whether a metadata part has text to show; absent and blank parts are left out of the line. */
export function hasMetaText(part: string | undefined): part is string {
  return !!part?.trim();
}

/** Joins the present metadata parts into one line, skipping absent or blank ones. */
export function joinMetaParts(parts: readonly (string | undefined)[]): string {
  return parts.filter(hasMetaText).join(META_SEPARATOR);
}

/** Formats a number as a percentage with up to two decimals, or an em dash when absent. */
export function formatRiskPercent(value: number | undefined): string {
  return value === undefined ? '—' : `${NUMBER_FORMATTER.format(value)}%`;
}

/** The signed, direction-aware gap to target, as a percentage. */
export const formatRiskGap = formatRiskPercent;
